import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { StoragePortalService } from './storage-portal.service';

export interface RmmMfaGrant { access_token: string; expires_at: string; recovery_codes?: string[]; }

interface StoredGrantSession {
  grant: RmmMfaGrant;
  session: string;
}

@Injectable({ providedIn: 'root' })
export class RmmMfaStateService {
  private static readonly STORAGE_KEY = 'rmm_mfa_grant_v2';
  private grant?: RmmMfaGrant;
  private session?: string;
  private timer?: ReturnType<typeof setTimeout>;

  constructor(private storage: StoragePortalService, private router: Router) {
    this.restore();
  }

  get valid(): boolean {
    if (!this.grant) {
      this.restore();
    }
    return !!this.grant && this.session === this.storage.token?.access_token && Date.parse(this.grant.expires_at) > Date.now();
  }

  get token(): string | undefined {
    return this.valid ? this.grant?.access_token : undefined;
  }

  accept(grant: RmmMfaGrant): void {
    this.clear();
    this.grant = grant;
    this.session = this.storage.token?.access_token;
    if (this.session) {
      try {
        const payload: StoredGrantSession = { grant, session: this.session };
        localStorage.setItem(RmmMfaStateService.STORAGE_KEY, JSON.stringify(payload));
      } catch {
        // Ignorar posibles errores de cuota en localStorage
      }
    }
    const msUntilExpiry = Math.max(0, Date.parse(grant.expires_at) - Date.now());
    this.timer = setTimeout(() => this.lock(), msUntilExpiry);
  }

  clear(): void {
    if (this.timer) clearTimeout(this.timer);
    this.grant = undefined;
    this.session = undefined;
    try {
      localStorage.removeItem(RmmMfaStateService.STORAGE_KEY);
    } catch {
      // Ignorar errores al acceder a localStorage
    }
  }

  lock(): void {
    this.clear();
    const path = this.router.url.split('?')[0];
    if (path === '/rmm' || path === '/auditoria' || path === '/tareas-motor') {
      void this.router.navigate(['/rmm-verificar'], { queryParams: { returnTo: path }, replaceUrl: true });
    }
  }

  private restore(): void {
    try {
      const raw = localStorage.getItem(RmmMfaStateService.STORAGE_KEY);
      if (!raw) return;
      const stored: StoredGrantSession = JSON.parse(raw);
      const currentToken = this.storage.token?.access_token;
      if (stored && stored.grant && stored.session && stored.session === currentToken && Date.parse(stored.grant.expires_at) > Date.now()) {
        this.grant = stored.grant;
        this.session = stored.session;
        const msUntilExpiry = Math.max(0, Date.parse(stored.grant.expires_at) - Date.now());
        if (this.timer) clearTimeout(this.timer);
        this.timer = setTimeout(() => this.lock(), msUntilExpiry);
      } else {
        localStorage.removeItem(RmmMfaStateService.STORAGE_KEY);
      }
    } catch {
      localStorage.removeItem(RmmMfaStateService.STORAGE_KEY);
    }
  }
}
